import { useEffect, useState } from "react";
import { X, CaretLeft, CaretRight, DownloadSimple, ArrowSquareOut, ImageSquare } from "@phosphor-icons/react";

/**
 * Full-screen photo viewer with carousel + download (eBay style).
 *
 * Props:
 *   open          : bool
 *   onClose       : () => void
 *   photos        : string[]  array of image URLs (data: URLs or http URLs)
 *   initialIndex? : number    index to open at (default 0)
 *   title?        : string    shown above thumbs
 *   driveUrl?     : string    optional Google Drive link shown as button
 *   filenameBase? : string    base name used when downloading (e.g. product name)
 */
export default function PhotoLightbox({ open, onClose, photos = [], initialIndex = 0, title = "", driveUrl = "", filenameBase = "foto" }) {
  const [index, setIndex] = useState(initialIndex);
  const safe = photos.filter(Boolean);
  const total = safe.length;

  useEffect(() => { if (open) setIndex(Math.min(initialIndex, Math.max(0, total - 1))); }, [open, initialIndex, total]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setIndex((i) => (i + 1) % total);
      else if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + total) % total);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, total]);

  if (!open) return null;
  const current = safe[index] || "";

  const download = async () => {
    if (!current) return;
    try {
      const safeName = (filenameBase || "foto").replace(/[^\w\-]+/g, "_").slice(0, 60);
      const ext = current.startsWith("data:image/") ? (current.match(/data:image\/(\w+)/)?.[1] || "png") : "jpg";
      const filename = `${safeName}_${index + 1}.${ext}`;
      let blob;
      if (current.startsWith("data:")) {
        const res = await fetch(current);
        blob = await res.blob();
      } else {
        const res = await fetch(current, { mode: "cors" });
        blob = await res.blob();
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // If CORS blocks the fetch (external URL), open in a new tab so user can save manually.
      window.open(current, "_blank", "noopener");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex flex-col"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="photo-lightbox"
    >
      <div className="flex items-center justify-between px-4 py-3 text-white text-sm" onClick={(e) => e.stopPropagation()}>
        <div className="truncate flex items-center gap-3">
          <span className="font-semibold truncate" title={title}>{title}</span>
          {total > 1 && <span className="font-mono-data text-xs text-zinc-400">{index + 1} / {total}</span>}
        </div>
        <div className="flex items-center gap-2">
          {driveUrl && (
            <a href={driveUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded">
              <ArrowSquareOut size={14}/> Ver carpeta Drive
            </a>
          )}
          {current && (
            <button onClick={download} className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded" data-testid="lightbox-download">
              <DownloadSimple size={14}/> Descargar
            </button>
          )}
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded" data-testid="lightbox-close" aria-label="Cerrar"><X size={20}/></button>
        </div>
      </div>

      <div
        className="flex-1 flex items-center justify-center relative overflow-hidden"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="lightbox-backdrop"
      >
        {total > 1 && (
          <button
            onClick={() => setIndex((i) => (i - 1 + total) % total)}
            className="absolute left-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Anterior"
            data-testid="lightbox-prev"
          >
            <CaretLeft size={22} weight="bold"/>
          </button>
        )}
        {current ? (
          <img src={current} alt={title} className="max-w-[92vw] max-h-[78vh] object-contain select-none" draggable={false} />
        ) : (
          <div className="text-center text-zinc-400 flex flex-col items-center gap-3">
            <ImageSquare size={48} />
            <span className="text-sm">Este producto no tiene foto subida</span>
            {driveUrl && <a href={driveUrl} target="_blank" rel="noopener noreferrer" className="text-xs underline">Abrir Drive →</a>}
          </div>
        )}
        {total > 1 && (
          <button
            onClick={() => setIndex((i) => (i + 1) % total)}
            className="absolute right-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Siguiente"
            data-testid="lightbox-next"
          >
            <CaretRight size={22} weight="bold"/>
          </button>
        )}
      </div>

      {total > 1 && (
        <div className="px-4 py-3 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-2 justify-center min-w-min">
            {safe.map((src, i) => (
              <button
                key={`${i}-${src.slice(-32)}`}
                onClick={() => setIndex(i)}
                className={`flex-shrink-0 w-16 h-16 border-2 rounded overflow-hidden ${i === index ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img src={src} alt={`thumb ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
